import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';
import { morph, MORPH_OPTIONS } from '@theme/morph';

const RUG_IMAGE_ATTRIBUTES = ['sizes', 'srcset', 'src', 'alt', 'style', 'data-max-resolution', 'loading', 'fetchpriority'];

function copyRugImageAttributes(source, target) {
  for (const attribute of RUG_IMAGE_ATTRIBUTES) {
    const value = source.getAttribute(attribute);
    if (value === null) {
      target.removeAttribute(attribute);
    } else {
      target.setAttribute(attribute, value);
    }
  }
}

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });

    this.#warmRugGalleryImages();
  }

  #controller = new AbortController();

  #rugWarmupRevision = 0;

  #rugUpdateRevision = 0;

  #rugPreloadImages = [];

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#rugUpdateRevision++;
    this.#cancelRugPreloads();
    this.#controller.abort();
  }

  /**
   * Handles a variant update event and reveals rug media in gallery order after each image is ready.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    const revision = ++this.#rugUpdateRevision;
    ++this.#rugWarmupRevision;
    this.#cancelRugPreloads();

    if (this.dataset.rugSmoothUpdate === 'true') {
      this.slideshow?.cancelPendingSelection();
      const stagedImages = this.#stageRugImages(newMediaGallery);
      if (!stagedImages) {
        this.replaceWith(newMediaGallery);
        return;
      }

      morph(this, newMediaGallery, {
        ...MORPH_OPTIONS,
        getNodeKey: (node) => {
          if (!(node instanceof HTMLElement)) return undefined;
          return (
            node.dataset.rugProgressSlot ||
            node.dataset.rugProgressMedia ||
            node.dataset.rugProgressImage ||
            node.dataset.mediaId ||
            node.getAttribute('slide-id') ||
            undefined
          );
        },
        onBeforeUpdate: (oldNode, newNode) => {
          MORPH_OPTIONS.onBeforeUpdate?.(oldNode, newNode);

          if (
            oldNode instanceof HTMLImageElement &&
            newNode instanceof HTMLImageElement &&
            oldNode.dataset.rugProgressImage &&
            oldNode.dataset.rugProgressImage === newNode.dataset.rugProgressImage
          ) {
            copyRugImageAttributes(oldNode, newNode);
          }
        },
      });
      this.updatedCallback();
      const slideshow = this.slideshow;
      if (slideshow) {
        slideshow.refreshAfterMorph();
        void slideshow.select(slideshow.initialSlideIndex, undefined, { animate: false, force: true });
      }
      void this.#revealRugImages(stagedImages, revision);
      return;
    }

    this.replaceWith(newMediaGallery);
  };

  #cancelRugPreloads() {
    for (const image of this.#rugPreloadImages) {
      image.removeAttribute('srcset');
      image.removeAttribute('src');
    }
    this.#rugPreloadImages = [];
  }

  #stageRugImages(newMediaGallery) {
    const useGrid = this.presentation === 'grid' && window.matchMedia('(min-width: 750px)').matches;
    const mainGroups = [
      ['slide', 'slideshow-component slideshow-slide:not([data-touch-video-slide])'],
      ['grid', '.media-gallery__grid > li'],
    ];
    const groups = mainGroups.map(([name, selector]) => ({
      name,
      oldSlots: Array.from(this.querySelectorAll(selector)),
      newSlots: Array.from(newMediaGallery.querySelectorAll(selector)),
    }));
    const primaryGroup = groups.find(({ name }) => name === (useGrid ? 'grid' : 'slide')) ?? groups[0];

    if (!primaryGroup?.newSlots.length) return null;

    for (const { oldSlots, newSlots } of groups) {
      if (oldSlots.length !== 0 && newSlots.length === 0) return null;
      for (let index = 0; index < Math.min(oldSlots.length, newSlots.length); index++) {
        if (!oldSlots[index]?.querySelector('img.product-media__image') ||
            !newSlots[index]?.querySelector('img.product-media__image')) return null;
      }
    }

    const thumbnails = Array.from(this.querySelectorAll('slideshow-controls[thumbnails]'));
    const newThumbnails = Array.from(newMediaGallery.querySelectorAll('slideshow-controls[thumbnails]'));
    if (thumbnails.length === newThumbnails.length) {
      thumbnails.forEach((controls, groupIndex) => {
        groups.push({
          name: `thumb-${groupIndex}`,
          oldSlots: Array.from(controls.querySelectorAll('.slideshow-controls__thumbnail')),
          newSlots: Array.from(newThumbnails[groupIndex].querySelectorAll('.slideshow-controls__thumbnail')),
        });
      });
    }

    const batches = Array.from({ length: Math.max(...groups.map(({ newSlots }) => newSlots.length)) }, () => []);
    const preloadImages = primaryGroup.newSlots.map((slot) => slot.querySelector('img.product-media__image')?.cloneNode(false));

    for (const { name, oldSlots, newSlots } of groups) {
      newSlots.forEach((newSlot, index) => {
        const newImage = newSlot.querySelector(name.startsWith('thumb-') ? 'img' : 'img.product-media__image');
        if (!(newImage instanceof HTMLImageElement)) return;

        const key = `${name}-${index}`;
        const oldSlot = oldSlots[index];
        const oldImage = oldSlot?.querySelector(name.startsWith('thumb-') ? 'img' : 'img.product-media__image');
        const sourceImage = /** @type {HTMLImageElement} */ (newImage.cloneNode(false));

        newSlot.dataset.rugProgressSlot = key;
        newImage.dataset.rugProgressImage = key;

        if (oldSlot instanceof HTMLElement && oldImage instanceof HTMLImageElement) {
          oldSlot.dataset.rugProgressSlot = key;
          oldImage.dataset.rugProgressImage = key;

          const oldMedia = oldSlot.querySelector('.product-media');
          const newMedia = newSlot.querySelector('.product-media');
          if (oldMedia instanceof HTMLElement && newMedia instanceof HTMLElement) {
            oldMedia.dataset.rugProgressMedia = key;
            newMedia.dataset.rugProgressMedia = key;
          }
        } else {
          newSlot.style.visibility = 'hidden';
        }

        batches[index]?.push({ key, sourceImage });
      });
    }

    return { batches, preloadImages };
  }

  async #revealRugImages({ batches, preloadImages }, revision) {
    const ready = batches.map((batch, index) => {
      const mainImage = preloadImages[index] ?? batch.find(({ key }) => !key.startsWith('thumb-'))?.sourceImage;
      const thumbnailImages = batch.filter(({ key }) => key.startsWith('thumb-')).map(({ sourceImage }) => sourceImage);
      return Promise.all([mainImage, ...thumbnailImages].map((sourceImage) => {
        if (!(sourceImage instanceof HTMLImageElement)) return Promise.resolve();

        const image = new Image();
        this.#rugPreloadImages.push(image);
        image.decoding = 'async';
        image.fetchPriority = index === 0 ? 'high' : 'low';
        image.sizes = sourceImage.getAttribute('sizes') ?? '';
        image.srcset = sourceImage.getAttribute('srcset') ?? '';
        image.src = sourceImage.getAttribute('src') ?? '';
        return image.decode().catch(() => undefined);
      }));
    });

    for (let index = 0; index < ready.length; index++) {
      await ready[index];
      if (revision !== this.#rugUpdateRevision || !this.isConnected) return;

      for (const { key, sourceImage } of batches[index] ?? []) {
        const targetImage = this.querySelector(`[data-rug-progress-image="${key}"]`);
        const slot = this.querySelector(`[data-rug-progress-slot="${key}"]`);
        if (targetImage instanceof HTMLImageElement) copyRugImageAttributes(sourceImage, targetImage);
        if (slot instanceof HTMLElement) slot.style.visibility = '';
      }
    }

    this.#rugPreloadImages = [];
  }

  #warmRugGalleryImages = () => {
    if (this.dataset.rugSmoothUpdate !== 'true') return;

    const revision = ++this.#rugWarmupRevision;
    const useGrid = this.presentation === 'grid' && window.matchMedia('(min-width: 750px)').matches;
    const activeGallery = useGrid ? this.querySelector('.media-gallery__grid') : this.refs.slideshow;
    const images = Array.from(activeGallery?.querySelectorAll('img.product-media__image') ?? []);
    const primaryImage = images[0];

    if (!(primaryImage instanceof HTMLImageElement) || images.length < 2) return;

    const loadImages = (pendingImages) => {
      if (revision !== this.#rugWarmupRevision) return;

      for (const image of pendingImages) {
        if (!(image instanceof HTMLImageElement)) continue;
        image.loading = 'eager';
        image.fetchPriority = 'low';
      }
    };

    const startWarmup = () => {
      if (revision !== this.#rugWarmupRevision) return;

      loadImages(images.slice(1, 3));
    };

    if (primaryImage.complete) {
      startWarmup();
    } else {
      primaryImage.addEventListener('load', startWarmup, { once: true, signal: this.#controller.signal });
      primaryImage.addEventListener('error', startWarmup, { once: true, signal: this.#controller.signal });
    }
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
